import { useEffect, useLayoutEffect, useRef, useState } from "react";

import type { SessionMessagesResponseMessagesInner } from "src/clients/agent-management";
import { chatTurns } from "src/unifiedCopilot/chat/chatTurns";

const FOLLOW_BOTTOM_THRESHOLD_PX = 50;
const SCROLL_KEYS = new Set([
  "PageUp",
  "PageDown",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

type SnapOrigin = { turns: number; queued: number };

type UseChatScrollParams = {
  listRef: React.RefObject<HTMLUListElement | null>;
  lastTurnRef: React.RefObject<HTMLLIElement | null>;
  messages: SessionMessagesResponseMessagesInner[];
  isActive: boolean;
  /** Stop-time drafts rendered after the turns; the last one anchors the pin. */
  queuedCount: number;
};

type UseChatScrollResult = {
  reservesTurnHeight: boolean;
  snapPromptToTop: () => void;
};

export const useChatScroll = ({
  listRef,
  lastTurnRef,
  messages,
  isActive,
  queuedCount,
}: UseChatScrollParams): UseChatScrollResult => {
  const [reservesTurnHeight, setReservesTurnHeight] = useState(false);

  const snapFromRef = useRef<SnapOrigin | null>(null);
  const hasSnappedRef = useRef(false);
  const aboveAnchorCountRef = useRef(0);
  const followsBottomRef = useRef(true);
  const isPinnedRef = useRef(false);
  const rafRef = useRef(0);

  const turns = chatTurns(messages);
  const turnCount = turns.length;
  const totalMessageCount = turns.reduce(
    (count, turn) => count + turn.messages.length,
    0,
  );
  const lastTurnMessageCount = turns.at(-1)?.messages.length ?? 0;
  // Everything rendered above the pin's anchor (the last queued draft, or the
  // last turn). A cancelled run's interrupted reply arriving late lands up
  // there and would push the anchor down; the pin compensates when it grows.
  const aboveAnchorCount =
    queuedCount > 0
      ? totalMessageCount + queuedCount - 1
      : totalMessageCount - lastTurnMessageCount;
  // Queued drafts postpone the release: between the stopped run's terminal
  // status and the flush, the cancelled turn may already carry its late
  // reply, which must not drop the pin before the flush engages it.
  const hasAnswer = queuedCount === 0 && lastTurnMessageCount > 1;

  const snapFrom = snapFromRef.current;
  // Either leg can grow alone: a queue submit adds a draft, while a flush or
  // a combined send swaps the drafts for one new turn.
  const isPinned =
    snapFrom !== null &&
    (turnCount > snapFrom.turns || queuedCount > snapFrom.queued);
  isPinnedRef.current = isPinned;

  // Set during render so the reservation is in the same commit as the snap;
  // scrollIntoView clamps to the space that exists when it runs.
  if (isPinned && !reservesTurnHeight) setReservesTurnHeight(true);
  if (turnCount + queuedCount === 0 && reservesTurnHeight) {
    setReservesTurnHeight(false);
  }

  const snapPromptToTop = () => {
    snapFromRef.current = { turns: turnCount, queued: queuedCount };
    hasSnappedRef.current = false;
    // The snap owns the scroll position from here; following the bottom would
    // yank the prompt off-screen once the pin releases.
    followsBottomRef.current = false;
  };

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    cancelAnimationFrame(rafRef.current);

    if (turnCount + queuedCount === 0) {
      hasSnappedRef.current = false;
      const armed = snapFromRef.current;
      // A snap armed at an empty transcript is the first prompt in flight,
      // not a reset.
      if (armed === null || armed.turns !== 0 || armed.queued !== 0) {
        snapFromRef.current = null;
        followsBottomRef.current = true;
      }
      return;
    }
    if (isPinned) {
      const anchor = lastTurnRef.current;
      if (!anchor) return;
      const isFirstSnap = !hasSnappedRef.current;
      // Content inside the anchor (the streaming answer) never re-snaps, so
      // the first snap's ease survives; content above it (a late reply, a
      // multi-draft flush collapsing bubbles) re-anchors pre-paint.
      const aboveChanged = aboveAnchorCountRef.current !== aboveAnchorCount;
      aboveAnchorCountRef.current = aboveAnchorCount;
      if (!isFirstSnap && !aboveChanged) return;
      hasSnappedRef.current = true;
      anchor.scrollIntoView({
        block: "start",
        // The first snap eases (a hidden tab has no frames to animate with);
        // compensation re-snaps must land pre-paint, within the same frame.
        behavior: isFirstSnap && !document.hidden ? "auto" : "instant",
      });
      return;
    }
    if (!followsBottomRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      list.scrollTo({ top: list.scrollHeight });
    });
  }, [
    isPinned,
    lastTurnRef,
    listRef,
    turnCount,
    queuedCount,
    messages,
    isActive,
    reservesTurnHeight,
  ]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    // Every way the user can move the list opens with one of these events;
    // the pin's own scroll emits only scroll events, ignored while pinned.
    const releasePin = () => {
      snapFromRef.current = null;
      isPinnedRef.current = false;
    };

    // A click on the messages lands on a child; only the scrollbar and the
    // list's own padding hit the list itself.
    const handlePointerDown = (event: Event) => {
      if (event.target === list) releasePin();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) releasePin();
    };

    const handleScroll = () => {
      if (isPinnedRef.current) return;
      followsBottomRef.current =
        list.scrollHeight - list.scrollTop - list.clientHeight <
        FOLLOW_BOTTOM_THRESHOLD_PX;
    };

    list.addEventListener("scroll", handleScroll);
    // releasePin never preventDefaults, so let the compositor scroll without
    // waiting on the main thread (listeners here default to non-passive).
    list.addEventListener("wheel", releasePin, { passive: true });
    list.addEventListener("touchmove", releasePin, { passive: true });
    list.addEventListener("pointerdown", handlePointerDown);
    list.addEventListener("keydown", handleKeyDown);
    return () => {
      list.removeEventListener("scroll", handleScroll);
      list.removeEventListener("wheel", releasePin);
      list.removeEventListener("touchmove", releasePin);
      list.removeEventListener("pointerdown", handlePointerDown);
      list.removeEventListener("keydown", handleKeyDown);
    };
  }, [listRef]);

  useEffect(() => {
    if (snapFromRef.current === null || isActive) return;
    if (hasAnswer) {
      snapFromRef.current = null;
      isPinnedRef.current = false;
    }
  }, [isActive, hasAnswer]);

  return { reservesTurnHeight, snapPromptToTop };
};
